import { DEFAULT_FORECAST_QUEUE_ROW_FILTERS } from './forecast-queue-filters'
import type { ForecastQueueRowFilters } from './forecast-queue-filters'

/**
 * The Forecasts view's working lists, in tab order (sc-1212 round 11).
 *
 * **Moved out of `forecasts/index.tsx` (sc-1212 reference pass)** so the
 * Reference tab can build one section per tab from the same array the tab
 * strip renders. The Reference tab keys its content with
 * `satisfies Record<ForecastListTabValue, …>`, so a tab added here without an
 * explanation is a type error rather than a silently undocumented list —
 * which is the failure the old hand-written Reference prose had: it covered
 * two of the nine tabs because nothing tied it to them.
 *
 * Urgent leads because it is the morning list and the reason the page exists;
 * the two delivered lists come last because they are a different population —
 * cases that have left the open stages — and were always below the queue when
 * these were stacked.
 *
 * Labels are shorter than the section headings they replace: a heading can
 * afford "Urgent — past the promised date", a tab cannot, and the section
 * inside each tab still carries the full wording. The Reference tab heads its
 * sections with these labels VERBATIM, so the words on screen match the tabs
 * above them.
 */
export const LIST_TABS = [
  // Urgent OVERLAPS the next two (sc-1212, Forecasts restructure): an urgent
  // case with a booked meeting is also in Scheduled, and one without is also in
  // Unscheduled Work. Intended -- Urgent is the morning list, the other two are
  // the same cases sorted by what to do next.
  { value: 'urgent', label: 'Urgent' },
  // The axis is "is a meeting booked", not "has the date passed": an open case
  // with a booked delivery meeting lives here, grouped by how late the work
  // is. See `forecast-queue-groups.ts`.
  { value: 'scheduled', label: 'Scheduled' },
  // Every case with nothing on the calendar: the open queue's unbooked rows
  // PLUS the delivered cases waiting on a next meeting. Replaces the two tabs
  // that used to be called "No date" and "Awaiting meeting".
  { value: 'unscheduled', label: 'Unscheduled Work' },
  // Was "Work due", then "Revisions expected". It now asks whether the PLANNER
  // has responded to a held meeting; the clock runs forward from the meeting.
  { value: 'workDue', label: 'Awaiting planner' },
  // Last of the work lists, because it is the only one that is finished work
  // rather than work owed -- and the one a manager visits to clear, not to plan.
  { value: 'readyToClose', label: 'Ready to close' },
  // After everything else, because it is the only tab that is not about work
  // owed at all (sc-1212 round 14). Every list above answers "what has to
  // happen next"; this one answers "what have we already done that nobody has
  // charged for", which is a different job on a different day.
  { value: 'postDelivery', label: 'Billable work' },
  // Cases a manager has set aside. After the lists that ask for action, because
  // it is the one a reader checks for what is coming BACK, not for what to do.
  { value: 'parked', label: 'Parked' },
  // Dead last, and it is the only tab that is not a work list at all
  // (sc-1212 audit round). Every tab above answers "what do we owe a client";
  // this one answers "which of our own records are malformed". Tyler named it:
  // *"add a tab for Cleaning"* -> **Needs Cleaned**.
  //
  // IT BUILDS, IT DOES NOT RECEIVE. *"i mean new list not moving an urgent to
  // cleaning if bad, we still need to work."* A case that is urgent AND broken
  // is on both tabs; nothing is reclassified out of the queue, because the
  // queue is what gets the client served.
  { value: 'cleaning', label: 'Needs Cleaned' },
] as const

export type ForecastListTabValue = (typeof LIST_TABS)[number]['value']

/**
 * One tab's label by its value.
 *
 * Derived from {@link LIST_TABS}, never hand-written, so the strip and every
 * other place that names a list — the Reference tab's headings and routine,
 * the Alerts view's "On list" column and its park copy — cannot disagree
 * about what a list is called.
 *
 * Safe as a plain `Record` index, unlike the API-enum maps on this page: these
 * keys are this module's own union, not a value that arrives over the wire.
 */
export const LIST_TAB_LABELS = Object.fromEntries(
  LIST_TABS.map((tab) => [tab.value, tab.label]),
) as Record<ForecastListTabValue, string>

/**
 * Which body the page is showing (sc-1212, Alerts view).
 *
 * Tyler, having seen the red-rows filter working: *"might be worth
 * dynamically changing the tabs views actually to a single Alerts view rather
 * than looking through tabs to find all the alerts, toggle just changes the
 * main view to the table of alerts and we have the Audit ability per row to
 * track we chased it or so we can leave a note."* Asked what the tabs should
 * do while it is on: **hide them.**
 *
 * - `lists` — the eight tabs. Exactly the page as it was.
 * - `alerts` — no tab strip at all, and one table of every alerted case.
 *
 * **A mode, not a tenth tab.** A tab would have to carry a count that is the
 * sum of eight others, sit in an order that means nothing beside them, and
 * leave the status pills above it filtering lists that are no longer on screen.
 * The toggle is also the only way back, which is why it keeps its place in the
 * filter card rather than moving into the strip it hides.
 */
export type ForecastViewMode = 'lists' | 'alerts'

export function forecastViewMode(alertsView: boolean): ForecastViewMode {
  return alertsView ? 'alerts' : 'lists'
}

/** Whether the tab strip and its bodies render at all. */
export function showsListTabs(mode: ForecastViewMode): boolean {
  return mode === 'lists'
}

/**
 * Whether the single Alerts table is the body.
 *
 * The inverse of {@link showsListTabs}, and worth its own name rather than a
 * `!` at the call site: the route reads
 * `showsAlertsTable(mode) ? <AlertsViewSection/> : <Tabs/>`, which says what is
 * on screen, where `!showsListTabs(mode)` says what is not.
 */
export function showsAlertsTable(mode: ForecastViewMode): boolean {
  return mode === 'alerts'
}

/**
 * The scope of the selection view (sc-1212): a goal or status selection is on
 * and NO tab has been chosen, so the body is one flat table drawn from several
 * lists.
 *
 * **It is a scope, not a tab.** It is not in {@link LIST_TABS}, so it cannot
 * leak into the Alerts view's source lists, the Reference tab's per-tab copy
 * or any `Record<ForecastListTabValue, …>`, each of which would then need an
 * entry for a list that does not exist. It is also never the value of the
 * page's `activeList` state, which stays the last REAL tab the reader was on
 * so that clearing the selection returns them there.
 *
 * Every question the page asks of a scope ("do the status cards show here?",
 * "does the goal button?", "is this row filter live?") has an explicit answer
 * for it rather than falling through to the default for an unknown string,
 * which for most of those questions is "no" and would have silently turned a
 * control off.
 */
export const SELECTION_SCOPE = 'all'

/**
 * The lists the selection view draws from.
 *
 * Parked is out on purpose, although a status selection CAN narrow it on its
 * own tab. The status cards count the open book (`statusPillCounts` reads
 * Urgent, Scheduled and Unscheduled, which a manager has not set aside), so a
 * flat table that also held parked cases would show a number the card above it
 * never promised: "Behind schedule 40" over a table of 47. The card and the
 * table agreeing is the property the page is built on; parked cases stay one
 * click away on their tab, which still narrows by status. Billable work and
 * Needs Cleaned have no status or goal to match.
 */
export const SELECTION_VIEW_TABS = [
  'urgent',
  'scheduled',
  'unscheduled',
  'workDue',
  'readyToClose',
] as const satisfies ReadonlyArray<ForecastListTabValue>

/**
 * Which tabs each row filter is LIVE on (sc-1212, filter-bar cleanup).
 *
 * **The invariant: a filter applies exactly where its control is visible.**
 * Never hidden-but-applied, never visible-but-inert. Tyler: *"I think the
 * filters need looked at."* Before this, every control showed on every tab and
 * most were inert on most of them, which reads as broken (§11).
 *
 * The map is MEASURED, not designed: each entry is the set of tabs whose
 * predicate actually reads that field.
 *
 * - `matchesForecastQueueRowFilters` (urgent, scheduled, unscheduled's open
 *   rows, parked) reads search, includeAnnualReviews, ownerIds, experiences,
 *   caseWorkKinds, unscheduledReasons.
 * - `matchesAwaitingNextMeetingFilters` (unscheduled's delivered group,
 *   workDue, readyToClose) reads search, includeAnnualReviews, ownerIds,
 *   experiences, caseWorkKinds.
 * - `matchesPostDeliveryWorkFilters` (postDelivery) reads search,
 *   includeAnnualReviews, ownerIds, plannerUserIds.
 * - `useCleaningFindings` (cleaning) takes search, ownerIds, plannerUserIds
 *   (plus its own problem chips); it has no Annual Review switch.
 *
 * **`unscheduledReasons` is deliberately NOT on `scheduled`, and that is the
 * subtle one.** The queue predicate does read it there, so it is not inert, it
 * is DESTRUCTIVE: every Scheduled row is booked, so its `unscheduledReason`
 * is null, so any selection empties the tab. Blanking it for that tab is what
 * makes hiding it correct rather than a trap.
 *
 * **The one deliberate exception is the stage pill row, and it is not a
 * `FILTER_TABS` key.** Tyler asked for the stage pills to *always show*, so on
 * Billable work and Needs Cleaned (`stagePillsNarrow` is false) they render
 * VISIBLY INERT: greyed, counts kept, and a reason on hover and in
 * `aria-describedby`. The invariant exists to stop a control that silently does
 * nothing, which reads as broken; an inert control that says so on screen does
 * not do that, so the exception is honest only because the inertness is
 * visible. A click there never appears to work and then fail to.
 *
 * There is no `submitWindows` key: the dropdown duplicated the Revisions
 * expected cutoff chips on the same axis, skewed their counts (it filtered
 * BEFORE they were counted), and emptied the delivered group on Unscheduled
 * Work (no meeting, no window). The chips are the one control for it.
 */
const QUEUE_TABS = [
  'urgent',
  'scheduled',
  'unscheduled',
  'workDue',
  'readyToClose',
  'parked',
] as const satisfies ReadonlyArray<ForecastListTabValue>

export const FILTER_TABS = {
  search: LIST_TABS.map((tab) => tab.value),
  ownerIds: LIST_TABS.map((tab) => tab.value),
  includeAnnualReviews: LIST_TABS.map((tab) => tab.value).filter(
    (tab) => tab !== 'cleaning',
  ),
  experiences: QUEUE_TABS,
  caseWorkKinds: QUEUE_TABS,
  unscheduledReasons: ['urgent', 'unscheduled', 'parked'],
  plannerUserIds: ['postDelivery', 'cleaning'],
} as const satisfies Record<
  keyof ForecastQueueRowFilters,
  ReadonlyArray<ForecastListTabValue>
>

/**
 * Whether `filter` is live in `scope` — the one question both the control's
 * visibility and the predicate's input are answered from, so they cannot
 * disagree.
 *
 * `scope` is the active tab value, or:
 * - `'alerts'`: the Alerts view draws from every work list at once (all but
 *   Needs Cleaned), so a filter is live there if it is live on ANY of them.
 * - `'all'` ({@link SELECTION_SCOPE}): the selection view draws from
 *   {@link SELECTION_VIEW_TABS}, so a filter is live if it is live on any of
 *   them, bar the no-meeting reason (see below).
 * - anything else (the Reference tab): no row list is on screen, so nothing is
 *   live and the filter controls are hidden.
 */
export function filterAppliesOn(
  filter: keyof ForecastQueueRowFilters,
  scope: string,
): boolean {
  const tabs: ReadonlyArray<string> = FILTER_TABS[filter]
  if (scope === 'alerts') return tabs.some((tab) => tab !== 'cleaning')
  if (scope === SELECTION_SCOPE) {
    // Live if it is live on ANY list the view draws from, except the reason
    // filter: it is destructive on the booked half of the view (a booked row's
    // reason is null, so any selection empties it -- the same hazard that
    // keeps it off Scheduled), and a goal selection is nothing BUT booked rows.
    // It is still one click away on Urgent and Unscheduled Work.
    if (filter === 'unscheduledReasons') return false
    return tabs.some((tab) =>
      (SELECTION_VIEW_TABS as ReadonlyArray<string>).includes(tab),
    )
  }
  return tabs.includes(scope)
}

/**
 * `filters` with every field that is not live in `scope` set back to its
 * default — **this is what makes hiding a control safe.** A hidden control
 * whose value still reached the predicate would strand the reader on an empty
 * list with nothing on screen to clear.
 *
 * Does not touch the stored selection: the caller keeps it, so coming back to
 * a tab where the control is live finds the value where it was left (and the
 * URL-backed `experience` / `person` params are never cleared by a tab
 * change).
 */
export function effectiveRowFilters(
  filters: ForecastQueueRowFilters,
  scope: string,
): ForecastQueueRowFilters {
  const result: ForecastQueueRowFilters = { ...filters }
  for (const key of Object.keys(FILTER_TABS) as Array<
    keyof ForecastQueueRowFilters
  >) {
    if (!filterAppliesOn(key, scope)) {
      Object.assign(result, {
        [key]: DEFAULT_FORECAST_QUEUE_ROW_FILTERS[key],
      })
    }
  }
  return result
}

/** Whether any row filter in `filters` is narrowing anything. */
export function hasActiveRowFilters(filters: ForecastQueueRowFilters): boolean {
  return (
    filters.search.trim().length > 0 ||
    filters.includeAnnualReviews ||
    filters.experiences.length > 0 ||
    filters.ownerIds.length > 0 ||
    filters.caseWorkKinds.length > 0 ||
    filters.unscheduledReasons.length > 0 ||
    filters.plannerUserIds.length > 0
  )
}

/**
 * The names of the filters a reader can actually clear on `scope`, as the
 * empty state's "Try clearing the …" sentence. Built from
 * {@link filterAppliesOn} so the copy cannot name a control that is hidden.
 * `extras` are the non-row controls the tab has (the status pills, the group
 * chips), which lead the list as they do on screen.
 */
export function filterClearHint(
  scope: string,
  extras: ReadonlyArray<string> = [],
): string {
  const named: ReadonlyArray<[keyof ForecastQueueRowFilters, string]> = [
    ['search', 'search'],
    ['experiences', 'experience'],
    ['ownerIds', 'owner'],
    ['caseWorkKinds', 'work kind'],
    ['unscheduledReasons', 'no-meeting reason'],
    ['plannerUserIds', 'planner'],
    ['includeAnnualReviews', 'Annual Review'],
  ]
  const names = [
    ...extras,
    ...named.filter(([key]) => filterAppliesOn(key, scope)).map(([, n]) => n),
  ]
  const last = names[names.length - 1]
  const list =
    names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${last}` : last
  return `Try clearing the ${list} filter.`
}
